import { ApiClientAxios, ProductType } from "@repo/api";
import { useCallback, useEffect, useMemo } from "react";
import type { UseQueryOptions } from "@tanstack/react-query";
import { fetchAllowedChanges, selectError, selectIsLoaded, selectIsLoading, useAppDispatch, useAppSelector } from "../redux";
import { store } from "../redux/rootStore";

type AllowedChangesItem = ApiClientAxios.AllowedChangesItem;
type GetAllowedChangesResponse = ApiClientAxios.GetAllowedChangesResponse;

export enum AllowedChangesQueryStatus {
    Error = "error",
    Pending = "pending",
    Success = "success",
}

export enum AllowedChangesFetchStatus {
    Fetching = "fetching",
    Idle = "idle",
}

type AllowedChangesQuerySnapshot = {
    data: AllowedChangesItem | undefined;
    error: Error | null;
    isError: boolean;
    isFetched: boolean;
    isFetching: boolean;
    isInitialLoading: boolean;
    isLoading: boolean;
    isPending: boolean;
    isRefetching: boolean;
    isSuccess: boolean;
    fetchStatus: AllowedChangesFetchStatus;
    status: AllowedChangesQueryStatus;
};

export type AllowedChangesQueryResult = AllowedChangesQuerySnapshot & {
    refetch: () => Promise<AllowedChangesQuerySnapshot>;
};

const applyContactProgressDefaults = (contract: AllowedChangesItem): AllowedChangesItem => ({
    ...contract,
    canChangeContactAddress:
        contract.isContactAddressChangeInProgress === true ? false : contract.canChangeContactAddress,
    canChangeElectronicContacts:
        contract.isElectronicContactsChangeInProgress === true ? false : contract.canChangeElectronicContacts,
});

const createDefaultAllowedChanges = (contractId: string): AllowedChangesItem => ({
    contractId,
    productType: ProductType.Na,
    hasAnyFailedChanges: false,
    isElectronicContactsChangeInProgress: false,
    isContactAddressChangeInProgress: false,
    canChengeInflationProgram: false,
    canChangeEmployerOrClientContribution: false,
    contributionDescription: undefined,
    canChangeContactAddress: false,
    contactAddressDescription: undefined,
    beneficiariesDescription: undefined,
    canChangeElectronicContacts: false,
    electronicContactsDescription: undefined,
    canChangeTaxOptimum: false,
    taxOptimumDescription: undefined,
    canChangeAmlIdentification: false,
    amlIdentificationDescription: undefined,
    canChangeIsEmployerContributing: false,
    isEmployerContributingDescription: undefined,
    pendingChanges: null,
    canChangeInvestmentStrategy: false,
    canChangeBeneficiaries: false,
    reasons: [],
    inflationDescription: "",
    isClientIdentified: false,
    isAmlFulfilled: false,
    isRiderWithoutNullableValuePUStart: false,
});

export const useAllowedChanges = ({
    id,
    options,
}: {
    id: string;
    options?: Partial<UseQueryOptions<GetAllowedChangesResponse, Error, AllowedChangesItem>>;
}): AllowedChangesQueryResult => {
    const dispatch = useAppDispatch();
    const enabled = options?.enabled ?? true;
    const originalData = useAppSelector((state) => (id ? state.allowedChanges.data[id] : undefined));
    const overrides = useAppSelector((state) => (id ? state.allowedChanges.overrides[id] : undefined));
    const loading = useAppSelector(selectIsLoading);
    const loaded = useAppSelector(selectIsLoaded);
    const errorMessage = useAppSelector(selectError);
    const error = useMemo(() => (errorMessage ? new Error(errorMessage) : null), [errorMessage]);
    const defaultData = useMemo(() => (id ? createDefaultAllowedChanges(id) : undefined), [id]);
    const data = useMemo(() => {
        if (!id) {
            return undefined;
        }

        if (!originalData) {
            return defaultData;
        }

        const effectiveOriginalData = applyContactProgressDefaults(originalData);

        if (!overrides) {
            return effectiveOriginalData;
        }

        return { ...effectiveOriginalData, ...overrides };
    }, [defaultData, id, originalData, overrides]);
    const hasResolvedData = Boolean(originalData);

    const toQuerySnapshot = useCallback(
        (
            resultData: AllowedChangesItem | undefined,
            resultError: Error | null,
            hasResultData: boolean,
        ): AllowedChangesQuerySnapshot => {
            let status = AllowedChangesQueryStatus.Pending;
            if (resultError) {
                status = AllowedChangesQueryStatus.Error;
            } else if (hasResultData) {
                status = AllowedChangesQueryStatus.Success;
            }

            return {
                data: resultData,
                error: resultError,
                isError: Boolean(resultError),
                isFetched: hasResultData || Boolean(resultError),
                isFetching: false,
                isInitialLoading: false,
                isLoading: false,
                isPending: !hasResultData && !resultError,
                isRefetching: false,
                isSuccess: hasResultData && !resultError,
                status,
                fetchStatus: AllowedChangesFetchStatus.Idle,
            };
        },
        [],
    );

    useEffect(() => {
        if (!enabled || !id) {
            return;
        }

        const state = store.getState();
        const isLoaded = selectIsLoaded(state);
        const isLoading = selectIsLoading(state);
       
        if (isLoaded || isLoading) {
            return;
        }

        dispatch(fetchAllowedChanges());
    }, [dispatch, enabled, id, loaded, loading]);

    const refetch = useCallback(async () => {
        if (!id || !enabled) {
            return toQuerySnapshot(data, error, hasResolvedData);
        }

        dispatch(fetchAllowedChanges({ force: true }));

        return toQuerySnapshot(data, error, hasResolvedData);
    }, [data, dispatch, enabled, error, hasResolvedData, id, toQuerySnapshot]);

    let status = AllowedChangesQueryStatus.Pending;
    if (error) {
        status = AllowedChangesQueryStatus.Error;
    } else if (hasResolvedData) {
        status = AllowedChangesQueryStatus.Success;
    }

    return {
        data,
        error,
        isError: Boolean(error),
        isFetched: loaded || hasResolvedData || Boolean(error),
        isFetching: loading,
        isInitialLoading: loading && !hasResolvedData,
        isLoading: loading && !hasResolvedData,
        isPending: !hasResolvedData && !error,
        isRefetching: loading && (loaded || hasResolvedData),
        isSuccess: hasResolvedData && !error,
        refetch,
        fetchStatus: loading ? AllowedChangesFetchStatus.Fetching : AllowedChangesFetchStatus.Idle,
        status,
    };
};
